import { apiFetch } from './api.js';

/**
 * The Master Data & Configuration Center (spec §13.5).
 *
 * One read for the whole configuration, because these values cross-reference
 * each other and a screen that assembles them from nine separate requests shows
 * a half-loaded configuration to somebody about to change it.
 */

export interface Organisation {
  id: string;
  name: string;
  defaultLocale: 'en' | 'ar';
  settings: Record<string, unknown>;
  /** The shortest password a local account may set (Rule 1). */
  passwordMinLength: number;
  /** Months entries stay in the live audit log before operations may archive them; null = no period set. */
  auditLogOnlineMonths: number | null;
  /** Reports: how far back a repeat nonconformity counts as a recurrence. */
  ncRecurrenceWindowMonths: number;
  /** Reports: the ageing bands in days, ascending. */
  auditAgeingBuckets: number[];
}

export interface WorkingCalendar {
  id: string;
  name: string;
  workingWeekdays: number[];
  timezone: string;
}

export interface Holiday {
  id: string;
  holidayDate: string;
  name: string;
}

export interface BusinessUnit {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
  headUserId: string | null;
}

export interface Discipline {
  id: string;
  code: string;
  name: string;
  sortOrder: number;
}

export interface Categorisation {
  id: string;
  app: string | null;
  category: string;
  label: string;
  riskLevel: 'low' | 'medium' | 'high' | null;
  sortOrder: number;
}

export interface Benchmark {
  id: string;
  app: string;
  metricKey: string;
  projectId: string | null;
  targetValue: string;
  comparison: 'gte' | 'lte';
}

export interface DistributionList {
  id: string;
  app: string;
  name: string;
  cadence: string;
  recipientUserIds: string[];
  recipientEmails: string[];
  reportKey: string | null;
  parameters: { projectId?: string };
  format: 'pdf' | 'xlsx' | 'csv';
  runTimeLocal: string;
  /** Whose authority a scheduled run uses: whoever last switched it on. */
  ownerUserId: string | null;
  isEnabled: boolean;
  lastRunAt: string | null;
  nextRunAt: string | null;
}

export interface DistributableReport {
  key: string;
  label: string;
  /** Sent when it is approved, not on a clock. */
  onApprovalOnly: boolean;
}

export interface DistributionRun {
  jobId: string | null;
  read: number;
  written: number;
  failed: number;
  outcome?: string;
  summary?: Record<string, unknown>;
}

export interface EscalationLevel {
  level: string;
  afterDays: number;
  repeatEveryDays?: number;
  recipientRoleKeys: string[];
}

export interface EscalationRule {
  id: string;
  app: string;
  name: string;
  description: string | null;
  triggerEvent: string;
  entityType: string;
  matchConditions: Record<string, unknown>;
  slaWorkingDays: number;
  /** Working days before the deadline at which the holder is reminded. */
  reminderOffsetDays: number[];
  /** Who holds the item: told at each reminder and at the breach. */
  reminderRecipientRoleKeys: string[];
  levels: EscalationLevel[];
  priority: string | null;
  upgradesToPriority: string | null;
  isEnabled: boolean;
}

export interface WeightSet {
  id: string;
  name: string;
  version: number;
  weights: Record<string, number>;
  ageingPenaltyPointsPerPct: string;
  ageingPenaltyCap: string;
  isProvisional: boolean;
  isDefault: boolean;
}

export interface MasterDataSnapshot {
  organisation: Organisation;
  calendar: WorkingCalendar | null;
  holidays: Holiday[];
  businessUnits: BusinessUnit[];
  disciplines: Discipline[];
  categorisations: Categorisation[];
  benchmarks: Benchmark[];
  distributionLists: DistributionList[];
  escalationRules: EscalationRule[];
  weightSets: WeightSet[];
  /** Synced from the Algihaz platform — never editable here (spec §13.5). */
  projects: {
    readOnly: true;
    source: string;
    rows: { id: string; code: string; name: string; businessUnitId: string | null }[];
  };
}

export const fetchMasterData = () => apiFetch<MasterDataSnapshot>('/admin/master-data');

const post = <T>(path: string, body: unknown) =>
  apiFetch<T>(`/admin/master-data${path}`, { method: 'POST', body: JSON.stringify(body) });

const patch = <T>(path: string, body: unknown) =>
  apiFetch<T>(`/admin/master-data${path}`, { method: 'PATCH', body: JSON.stringify(body) });

export const updateOrganisation = (body: Record<string, unknown>) =>
  patch<{ organisation: Organisation }>('/organisation', body);

export const updateCalendar = (id: string, body: Record<string, unknown>) =>
  patch<{ calendar: WorkingCalendar }>(`/calendars/${id}`, body);

export const addHoliday = (calendarId: string, body: { holidayDate: string; name: string }) =>
  post<{ holiday: Holiday }>(`/calendars/${calendarId}/holidays`, body);

export const createBusinessUnit = (body: Record<string, unknown>) =>
  post<{ businessUnit: BusinessUnit }>('/business-units', body);

export const createDiscipline = (body: Record<string, unknown>) =>
  post<{ discipline: Discipline }>('/disciplines', body);

export const createCategorisation = (body: Record<string, unknown>) =>
  post<{ categorisation: Categorisation }>('/categorisations', body);

export const createBenchmark = (body: Record<string, unknown>) =>
  post<{ benchmark: Benchmark }>('/benchmarks', body);

export const updateBenchmark = (id: string, body: Record<string, unknown>) =>
  patch<{ benchmark: Benchmark }>(`/benchmarks/${id}`, body);

export const updateEscalationRule = (id: string, body: Record<string, unknown>) =>
  patch<{ rule: EscalationRule }>(`/escalation-rules/${id}`, body);

export const createWeightSet = (body: Record<string, unknown>) =>
  post<{ weightSet: WeightSet }>('/pqi-weight-sets', body);

export const retire = (kind: string, id: string) =>
  apiFetch<{ retired: { id: string } }>(`/admin/master-data/${kind}/${id}`, { method: 'DELETE' });

export const fetchDistributableReports = (app: string) =>
  apiFetch<{ reports: DistributableReport[] }>(`/admin/master-data/distribution-lists/reports?app=${encodeURIComponent(app)}`);

export const createDistributionList = (body: Record<string, unknown>) =>
  post<{ distributionList: DistributionList }>('/distribution-lists', body);

export const updateDistributionList = (id: string, body: Record<string, unknown>) =>
  patch<{ distributionList: DistributionList }>(`/distribution-lists/${id}`, body);

export const runDistributionList = (id: string) =>
  post<{ run: DistributionRun }>(`/distribution-lists/${id}/run`, {});
